import banner1 from '@assets/banner1.png';
import banner2 from '@assets/banner2.png';
import { Box } from '@components/ui/atoms/Box';
import { Button } from '@components/ui/atoms/Button';
import { Flex } from '@components/ui/atoms/Flex';
import { Image } from '@components/ui/atoms/Image';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { DialogModal } from '@components/ui/molecules/DialogModal';
import React, { useEffect, useState } from 'react';

import { DASHBOARD_BANNER_COMPLETED_KEY } from './DashboardBanner.constants';

interface BannerStep {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  features: string[];
}

const STEPS: BannerStep[] = [
  {
    title: '👋 Welcome!',
    subtitle: 'Bhutan Payments has evolved.',
    description:
      "You're now using Version 2—built for speed, reliability, and deeper visibility across payments and payouts.",
    image: banner1,
    features: [
      'More ways to pay: cards, digital wallets, and ACH (with rates from 2.2% - 4.99%)',
      'Get paid every 2–3 business days with twice-weekly payouts',
      'Higher conversion rate, so you can capture more revenue',
      'A faster, simpler design built around how you work',
      'Clear roles and access controls to manage your team',
    ],
  },
  {
    title: 'Bhutan Payments is now available to all industries, not just travel.',
    subtitle: '',
    description:
      'Our mission is to deliver a Bhutan-tailored, modern payment platform with innovative features that support merchants as they grow. We’ll continue introducing new features and capabilities in the months ahead.',
    image: banner2,
    features: [],
  },
];

export const DashboardBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const isCompleted = localStorage.getItem(DASHBOARD_BANNER_COMPLETED_KEY);

    if (!isCompleted) {
      setIsOpen(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    localStorage.setItem(DASHBOARD_BANNER_COMPLETED_KEY, 'true');
    setIsOpen(false);
  };

  if (!isOpen) {
    return <>{children}</>;
  }

  const step = STEPS[currentStep];

  return (
    <>
      <DialogModal
        hideHeader={true}
        closeOnInteractOutside={false}
        isOpen={isOpen}
        onClose={handleComplete}
        title=""
        maxWidth="520px"
      >
        <Stack gap="0">
          <Box
            p="20px"
            bg="linear-gradient(135deg, #d2c4faff, #f6b5daff, #fafeb8ff, #9ddef9ff)"
            borderTopLeftRadius="xl"
            borderTopRightRadius="xl"
            mt="-24px"
            mx="-24px"
            height="auto"
          >
            <Box borderRadius="lg" overflow="hidden" width="full" height="full" bg="white">
              <Image
                src={step.image}
                alt="Dashboard Banner"
                width="full"
                height="full"
                objectFit="cover"
              />
            </Box>
          </Box>

          <Stack pt="32px" gap="24px" textAlign="left">
            <Stack gap="16px">
              <Text fontSize="20px" fontWeight="700" color="gray.800" lineHeight="1.4">
                {step.title}
              </Text>
              <Stack gap="4px">
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  {step.subtitle}
                </Text>
                <Text fontSize="sm" color="gray.500" lineHeight="1.5">
                  {step.description}
                </Text>
              </Stack>
            </Stack>

            {currentStep === 0 && (
              <Stack gap="12px">
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  What&apos;s new in V2
                </Text>
                <Stack gap="8px">
                  {step.features.map((feature, i) => (
                    <Flex key={i} gap="12px" alignItems="flex-start">
                      <Text color="gray.400" mt="-2px">
                        •
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {feature}
                      </Text>
                    </Flex>
                  ))}
                </Stack>
              </Stack>
            )}

            <Flex justifyContent="space-between" alignItems="center">
              <Button
                visual="primary"
                onClick={handleNext}
                height="36px"
                borderRadius="lg"
                fontSize="sm"
                px="8"
                fontWeight="500"
              >
                {currentStep === STEPS.length - 1 ? 'Start Your Journey' : 'Next'}
              </Button>
            </Flex>
          </Stack>
        </Stack>
      </DialogModal>
      {children}
    </>
  );
};
